import { booleanAttribute, Component, inject, input, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { PaywallCardsViewComponent } from "../paywall-cards-view/paywall-cards-view.component";
import { PaywallCheckoutComponent } from "../paywall-checkout/paywall-checkout.component";
import { PaywallService } from '../../services/paywall.service';
import { CheckoutService } from '../../services/checkout.service';
import { PaywallSettings } from '../../models/paywall-settings';
import { filter, fromEvent, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaywallSuccessComponent } from "../paywall-success/paywall-success.component";
import { PaywallContactComponent } from "../paywall-contact/paywall-contact.component";
import { PaywallContactSuccessComponent } from "../paywall-contact-success/paywall-contact-success.component";
import { PaywallSimulatePaymentComponent } from "../paywall-simulate-payment/paywall-simulate-payment.component";
import { loadErrorReasons } from '../../helpers/http-error.helper';


@Component({
  selector: 'yon-pw-paywall-main',
  imports: [
    PaywallCardsViewComponent,
    PaywallCheckoutComponent,
    PaywallSuccessComponent,
    PaywallContactComponent,
    PaywallContactSuccessComponent,
    PaywallSimulatePaymentComponent
  ],
  templateUrl: './paywall-main.component.html',
  styleUrl: './paywall-main.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom
})
export class PaywallMainComponent {
  token = input<string>();
  apiUrl = input<string>();
  preview = input(false, { transform: booleanAttribute });
  previewToken = input<string>();
  paywallService: PaywallService = inject(PaywallService);
  checkoutService: CheckoutService = inject(CheckoutService);
  paywallSettings: WritableSignal<PaywallSettings | undefined> = signal(undefined);

  showError = signal(false);
  /** Why the paywall could not load; only filled in the editor's preview. */
  loadErrorDetails = signal<string[]>([]);
  isPaywallLoaded = signal(false);

  constructor() {
    // Back from the Stripe page shows this page from the browser's back/forward cache, with the hub connection closed.
    fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(
      filter(event => event.persisted),
      takeUntilDestroyed()
    ).subscribe(() => this.checkoutService.resumeAfterPageRestore());
  }

  ngOnInit() {
    this.paywallService.setApiUrl(this.apiUrl());
    this.paywallService.isPreview.set(this.preview());
    this.paywallService.previewToken.set(this.previewToken() ?? '');

    if (this.token() === undefined || this.token() === '') {
      console.error('Paywall token is required');
      return;
    }

    const decodedToken = atob(this.token() as string);
    const parts = decodedToken.split(',');
    if (parts.length !== 3) {
      console.error('Invalid token format');
      return;
    }
    const [pwid, leid, teid] = parts;

    this.paywallService.getSettings(pwid, leid, teid).pipe(take(1)).subscribe((settings: any) => {
      const paywall = settings.paywall;
      if (paywall && !paywall.products?.length && paywall.product) {
        paywall.products = [paywall.product];
      }
      this.paywallSettings.set(paywall);
    }, (e: any) => {
      this.showLoadError('Could not load the paywall settings', e);
    }, () => {
      this.isPaywallLoaded.set(true);
    });

    this.paywallService.getCountries(pwid, leid, teid).pipe(take(1)).subscribe((countries: any[]) => {
    }, (error: any) => {
      this.showLoadError('Could not load the countries', error);
    });

    if (location.href.endsWith('#success')) {
      this.paywallService.step.set('success');
    }

    // The buyer left the Stripe page without paying: let the backend close the checkout and drop its draft order.
    if (location.href.endsWith('#cancel') && localStorage.getItem('sessionId')) {
      this.checkoutService.cancelPurchaseSubscription().pipe(take(1)).subscribe({
        next: () => localStorage.removeItem('sessionId'),
        error: (error: any) => console.error('Could not cancel the checkout:', error)
      });
    }

  }

  // Buyers only see a generic message; the technical details (URL, status, validation errors) go to the console.
  // The editor's preview also lists the reasons, so the admin can see what to set up.
  private showLoadError(context: string, error: unknown): void {
    console.error(`Paywall: ${context}.`, error);
    if (this.paywallService.isPreview()) {
      this.loadErrorDetails.update(current => [...new Set([...current, ...loadErrorReasons(error)])]);
    }
    this.showError.set(true);
  }
}
